import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { CheckCircle2, ShieldAlert, ShieldCheck, XCircle } from 'lucide-react';
import { Botao, Cartao } from '../componentes/base';
import { Carregando, Erro, Vazio } from '../componentes/estados';
import { Rotulo, Selecao } from '../componentes/formulario';
import { TextoCortado } from '../componentes/texto-cortado';
import { ErroRequisicao, get, mensagemDeErro, post } from '../nucleo/api';
import { dataHora } from '../nucleo/formato';
import { rotuloModulo } from '../nucleo/rotulos';

/**
 * Lote de 19/10 (item 14): tela de consentimento do OAuth do MCP. O `/oauth/authorize` da API confere o cliente e o
 * endereço de volta e manda o navegador para cá (com a sessão normal: sem login, o Protegido leva ao login, com TOTP, e volta).
 * Mostra o cliente, para onde ele volta, o workspace, a empresa e os módulos que a pessoa pode conceder; aprovar devolve o
 * endereço de volta com o código de uso único. O pedido com cliente ou endereço de volta desconhecido nunca redireciona.
 */
interface DadosAutorizacao {
  ligado: boolean;
  cliente: { id: string; nome: string; redirect_host: string; registrado_em: string };
  workspace: { id: string; nome: string };
  empresa: { nome: string } | null;
  modulos: { slug: string; nome: string; pode_escrever: boolean }[];
  pedidos: string[];
  csrf: string;
}

const PARAMETROS = ['response_type', 'client_id', 'redirect_uri', 'code_challenge', 'code_challenge_method', 'state', 'scope', 'resource'] as const;
const ERROS = new Set(['cliente_invalido', 'redirect_invalido', 'pedido_invalido']);

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <dt className="shrink-0 text-xs text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-sm break-words text-tinta-1 sm:text-right">{children}</dd>
    </div>
  );
}

/** Escolha inicial: o que o cliente pediu (dentro do que a pessoa pode); sem pedido válido, leitura de tudo que ela lê. */
export function escolhaInicial(d: Pick<DadosAutorizacao, 'modulos' | 'pedidos'>): Record<string, '' | 'leitura' | 'escrita'> {
  const r: Record<string, '' | 'leitura' | 'escrita'> = {};
  for (const m of d.modulos) r[m.slug] = d.pedidos.length ? '' : 'leitura';
  for (const p of d.pedidos) {
    const [m, tipo] = p.split(':') as [string, 'leitura' | 'escrita'];
    if (m in r && (r[m] !== 'escrita' || tipo === 'escrita')) r[m] = tipo;
  }
  return r;
}

function Falha({ titulo, texto }: { titulo: string; texto: string }) {
  return (
    <Cartao>
      <Vazio icone={<ShieldAlert className="size-5 text-perigo" aria-hidden />} titulo={titulo} texto={texto} />
    </Cartao>
  );
}

function Consentimento({ d, pedido }: { d: DadosAutorizacao; pedido: Record<string, string> }) {
  const { t } = useTranslation();
  const [escolha, setEscolha] = useState(() => escolhaInicial(d));
  const [erro, setErro] = useState<string | null>(null);
  const [destino, setDestino] = useState<{ url: string; aprovado: boolean } | null>(null);
  const escopos = useMemo(() => Object.entries(escolha).flatMap(([m, v]) => (v ? [`${m}:${v}`] : [])), [escolha]);
  const decidir = useMutation({
    mutationFn: (decisao: 'aprovar' | 'recusar') => post<{ destino: string }>('/oauth/autorizacao', { ...pedido, escopos: decisao === 'aprovar' ? escopos : [], decisao, csrf: d.csrf }),
    onSuccess: (r, decisao) => {
      setErro(null);
      setDestino({ url: r.destino, aprovado: decisao === 'aprovar' });
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  // O endereço de volta é o que o cliente registrou (conferido no servidor, comparação exata).
  useEffect(() => {
    if (destino) window.location.assign(destino.url);
  }, [destino]);

  if (destino) {
    return (
      <Cartao>
        <Vazio
          icone={destino.aprovado ? <CheckCircle2 className="size-5 text-verde" aria-hidden /> : <XCircle className="size-5" aria-hidden />}
          titulo={destino.aprovado ? t('assistentes.oauth.autorizado') : t('assistentes.oauth.recusado')}
          texto={t('assistentes.oauth.voltando', { cliente: d.cliente.nome })}
        />
      </Cartao>
    );
  }
  return (
    <Cartao
      titulo={
        <span className="flex min-w-0 items-center gap-2">
          <ShieldCheck className="size-4 shrink-0 text-tinta-3" aria-hidden />
          <TextoCortado linhas={2}>{t('assistentes.oauth.pedido', { cliente: d.cliente.nome })}</TextoCortado>
        </span>
      }
    >
      <div className="flex flex-col gap-5" data-consentimento-oauth>
        <dl className="flex flex-col gap-2">
          <Linha rotulo={t('assistentes.oauth.cliente')}>
            <TextoCortado>{d.cliente.nome}</TextoCortado>
          </Linha>
          <Linha rotulo={t('assistentes.oauth.volta')}>
            <span className="numero text-xs">{d.cliente.redirect_host}</span>
          </Linha>
          <Linha rotulo={t('assistentes.oauth.registrado')}>{dataHora(d.cliente.registrado_em)}</Linha>
          <Linha rotulo={t('assistentes.oauth.workspace')}>
            <TextoCortado>{d.workspace.nome}</TextoCortado>
          </Linha>
          <Linha rotulo={t('assistentes.oauth.empresa')}>{d.empresa ? <TextoCortado>{d.empresa.nome}</TextoCortado> : t('assistentes.oauth.todas')}</Linha>
        </dl>
        {!d.ligado ? (
          <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-sm text-laranja">
            {t('assistentes.oauth.desligado')}
          </p>
        ) : (
          <fieldset className="flex flex-col gap-3">
            <legend className="kicker mb-1">{t('assistentes.oauth.escopos')}</legend>
            <p className="text-sm text-tinta-3">{t('assistentes.oauth.escoposAjuda')}</p>
            <div className="grid gap-3 sm:grid-cols-2" data-escopo-modulos>
              {d.modulos.map((m) => (
                <Rotulo key={m.slug} id={`oauth-escopo-${m.slug}`} rotulo={rotuloModulo(m.slug, m.nome)}>
                  <Selecao
                    id={`oauth-escopo-${m.slug}`}
                    value={escolha[m.slug] ?? ''}
                    onChange={(e) => setEscolha({ ...escolha, [m.slug]: e.target.value as '' | 'leitura' | 'escrita' })}
                    opcoes={[
                      { valor: '', rotulo: t('assistentes.form.semAcesso') },
                      { valor: 'leitura', rotulo: t('assistentes.form.leitura') },
                      ...(m.pode_escrever ? [{ valor: 'escrita', rotulo: t('assistentes.form.escrita') }] : []),
                    ]}
                  />
                </Rotulo>
              ))}
            </div>
            <p className="text-xs text-tinta-3">{t('assistentes.oauth.assistente')}</p>
          </fieldset>
        )}
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <Botao icone={<XCircle className="size-4" aria-hidden />} carregando={decidir.isPending && decidir.variables === 'recusar'} onClick={() => decidir.mutate('recusar')}>
            {t('assistentes.oauth.recusar')}
          </Botao>
          {d.ligado && (
            <Botao
              variante="primario"
              icone={<ShieldCheck className="size-4" aria-hidden />}
              carregando={decidir.isPending && decidir.variables === 'aprovar'}
              disabled={!escopos.length}
              title={!escopos.length ? t('assistentes.form.escolhaModulo') : undefined}
              onClick={() => decidir.mutate('aprovar')}
            >
              {t('assistentes.oauth.aprovar')}
            </Botao>
          )}
        </div>
      </div>
    </Cartao>
  );
}

export function AutorizarOAuth() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const erroDaUrl = params.get('erro');
  const pedido = useMemo(() => {
    const p: Record<string, string> = {};
    for (const k of PARAMETROS) {
      const v = params.get(k);
      if (v !== null) p[k] = v;
    }
    return p;
  }, [params]);
  const temPedido = !!pedido.client_id && !!pedido.redirect_uri;
  const q = useQuery({
    queryKey: ['oauth', 'autorizacao', pedido],
    queryFn: () => get<DadosAutorizacao>(`/oauth/autorizacao?${new URLSearchParams(pedido).toString()}`),
    enabled: !erroDaUrl && temPedido,
    retry: false,
    gcTime: 0,
  });
  const codigoErro = erroDaUrl && ERROS.has(erroDaUrl) ? erroDaUrl : 'pedido_invalido';
  const motivo = q.error instanceof ErroRequisicao && q.error.corpo.code === 'oauth.pedido_invalido' ? String(q.error.metaDoErro()?.motivo ?? '') : null;
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <header className="min-w-0">
        <p className="kicker">{t('assistentes.oauth.kicker')}</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight">{t('assistentes.oauth.titulo')}</h1>
      </header>
      {erroDaUrl || !temPedido ? (
        <Falha titulo={t('assistentes.oauth.erroTitulo')} texto={t(`assistentes.oauth.erros.${codigoErro}`)} />
      ) : q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        motivo !== null ? (
          <Falha titulo={t('assistentes.oauth.erroTitulo')} texto={t(`assistentes.oauth.erros.${ERROS.has(motivo) ? motivo : 'pedido_invalido'}`)} />
        ) : (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        )
      ) : (
        <Consentimento d={q.data} pedido={pedido} />
      )}
    </div>
  );
}
